import { getPlacesByGeohash, getPlacesByQuery } from "@/services/place";
import { UseQueryOptions, useQuery } from "@tanstack/react-query";
import { Place } from "../types";
import { captureException } from "@sentry/react";

export function useGetPlacesByName(
  searchTerm: string,
  geohash?: string | null,
  options?: UseQueryOptions<unknown, Error, Place[]>,
) {
  const fetchPlaces = async (): Promise<Place[]> => {
    // 1) If only `geohash` is set (and `searchTerm` is empty), filter by geohash alone.
    if (geohash && searchTerm === "") {
      return await getPlacesByGeohash(geohash);
    }

    // 2) Otherwise, fall back to the plain name-based query, if term has at least 3 characters.
    if (searchTerm.length >= 4) {
      return await getPlacesByQuery(searchTerm);
    }

    return [];
  };

  return useQuery<unknown, Error, Place[]>({
    queryKey: ["places", searchTerm],
    queryFn: fetchPlaces,
    ...options,
    retry(failureCount, error) {
      captureException(error);
      if (error instanceof Error && error.message === "Place not found") {
        return false;
      }
      return failureCount < 3;
    },
  });
}
